import {
  ANNOTATION_LOCATION,
  stringifyEntityRef,
} from "@backstage/catalog-model";
import { HeaderIconLinkRow } from "@backstage/core-components";
import { alertApiRef, errorApiRef, useApi } from "@backstage/core-plugin-api";
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from "@backstage/integration-react";
import { AboutContent } from "@backstage/plugin-catalog";
import { catalogEntityRefreshPermission } from "@backstage/plugin-catalog-common/alpha";
import {
  catalogApiRef,
  getEntitySourceLocation,
  useEntity,
} from "@backstage/plugin-catalog-react";
import { useEntityPermission } from "@backstage/plugin-catalog-react/alpha";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CardHeader from "@material-ui/core/CardHeader";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import CachedIcon from "@material-ui/icons/Cached";
import EditIcon from "@material-ui/icons/Edit";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { resolveSaasFilePath } from "./resolveSaasFilePath";

const useStyles = makeStyles({
  gridItemCard: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100% - 10px)",
    marginBottom: "10px",
  },
  gridItemCardContent: {
    flex: 1,
  },
});

type Variant = "gridItem" | "fullHeight";

interface Props {
  variant?: Variant;
}

/**
 * Tarjeta About para componentes SaaS (spec.type=external-service con tag saas).
 * Al pulsar el lápiz navega al template edit-saas con saasEntity y filePath
 * (resuelto de managed-by-location) precargados como readonly.
 */
export const EntityAboutSaaSCard = ({ variant = "gridItem" }: Props) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);

  const { allowed: canRefresh } = useEntityPermission(
    catalogEntityRefreshPermission,
  );

  // ── Refresh ────────────────────────────────────────────────────────────────
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];
  const allowRefresh =
    entityLocation?.startsWith("url:") || entityLocation?.startsWith("file:");

  const refreshEntity = useCallback(async () => {
    try {
      await catalogApi.refreshEntity(stringifyEntityRef(entity));
      alertApi.post({
        message: "Refresco programado, los cambios se aplicarán en breve.",
        severity: "info",
        display: "transient",
      });
    } catch (e) {
      errorApi.post(e as Error);
    }
  }, [catalogApi, entity, alertApi, errorApi]);

  // ── Editar SaaS ────────────────────────────────────────────────────────────
  const handleEdit = () => {
    const metadata = entity.metadata;
    const spec = entity.spec as Record<string, unknown> | undefined;
    const location = metadata.annotations?.[ANNOTATION_LOCATION];
    const filePath = resolveSaasFilePath(location);

    // edit-saas precarga filePath y saasEntity como readonly: si no se puede
    // derivar el fichero de forma segura, el wizard sería un callejón sin
    // salida → fallo claro en lugar de abrirlo incompleto.
    if (!filePath) {
      errorApi.post(
        new Error(
          `No se ha podido determinar el fichero del catálogo (saas/*.yaml) para esta entidad a partir de su ubicación (${location ?? "sin location"}). Revisa la annotation backstage.io/managed-by-location.`,
        ),
      );
      return;
    }

    // Fichas legacy escriben spec.system como slug pelado ("github"); el
    // EntityPicker del wizard exige la ref completa → cualificar. Ya
    // cualificadas ("system:default/github") pasan intactas.
    const rawSystem = (spec?.system as string) ?? "";
    const system =
      rawSystem && !rawSystem.includes(":")
        ? `system:default/${rawSystem}`
        : rawSystem;

    const formData = {
      saasEntity: stringifyEntityRef(entity),
      filePath,
      title: metadata.title ?? "",
      description: metadata.description ?? "",
      tags: metadata.tags ?? [],
      links: metadata.links ?? [],
      owner: (spec?.owner as string) ?? "",
      system,
      lifecycle: (spec?.lifecycle as string) ?? "",
    };

    const queryParams = new URLSearchParams({
      formData: JSON.stringify(formData),
    });
    navigate(`/create/templates/default/edit-saas?${queryParams}`);
  };

  // ── Subheader: enlace a fuente SCM + Documentación ──────────────────────────
  const entitySourceLocation = getEntitySourceLocation(
    entity,
    scmIntegrationsApi,
  );
  const docLink = entity.metadata.links?.find(
    (link) => link.icon === "docs" || link.title?.toLowerCase().includes("doc"),
  )?.url;

  const subheaderLinks = [
    ...(entitySourceLocation
      ? [
          {
            label: "Ver fuente",
            disabled: false,
            icon: (
              <ScmIntegrationIcon type={entitySourceLocation.integrationType} />
            ),
            href: entitySourceLocation.locationTargetUrl,
          },
        ]
      : []),
    ...(docLink
      ? [
          {
            label: "Documentación",
            disabled: false,
            icon: <OpenInNewIcon fontSize="small" />,
            href: docLink,
          },
        ]
      : []),
  ];

  return (
    <Card className={variant === "gridItem" ? classes.gridItemCard : undefined}>
      <CardHeader
        title="Sobre este servicio SaaS"
        action={
          <>
            {allowRefresh && canRefresh && (
              <IconButton
                aria-label="Refrescar entidad"
                title="Reprocesar entidad en el catálogo"
                onClick={refreshEntity}
              >
                <CachedIcon />
              </IconButton>
            )}
            <IconButton
              aria-label="Editar SaaS"
              title="Editar los metadatos de este servicio SaaS"
              onClick={handleEdit}
            >
              <EditIcon />
            </IconButton>
          </>
        }
        subheader={
          subheaderLinks.length > 0 ? (
            <HeaderIconLinkRow links={subheaderLinks} />
          ) : undefined
        }
      />
      <Divider />
      <CardContent
        className={
          variant === "gridItem" ? classes.gridItemCardContent : undefined
        }
      >
        <AboutContent entity={entity} />
      </CardContent>
    </Card>
  );
};
